import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';

import { axiosMailPostCall } from 'src/services/apisCall';
import { renderErrorMessage, renderSuccessMessage } from '../../lib/toastMessage';

const columns = [
  { name: 'timeout', label: 'Timeout' },
  { name: 'disallowed_extensions', label: 'Disallowed Extensions' },
  { name: 'default_file_size', label: 'Default File Size' },
  { name: 'archive_max_depth', label: 'Archive Max Depth' },
  { name: 'archive_extensions', label: 'Archive Extensions' },
];

const FormData = {
  timeout: '',
  disallowed_extensions: '',
  default_file_size: '',
  allowed_file_size: '',
  archive_allow_encrypted: 'no',
  archive_max_depth: '',
  archive_extensions: '',
  type: '-',
};

const EmailUpdate = ({ open, handleClose, record, onUpdate }) => {
  const [formData, setFormData] = useState(FormData);

  useEffect(() => {
    if (record) {
      setFormData({
        timeout: record.timeout || '',
        disallowed_extensions: record.disallowed_extensions || '',
        default_file_size: record.default_file_size || '',
        allowed_file_size: JSON.stringify(safeJsonParse(record.allowed_file_size), null, 2),
        archive_allow_encrypted: record.archive_allow_encrypted ? 'yes' : 'no',
        archive_max_depth: record.archive_max_depth || '',
        archive_extensions: record.archive_extensions || '',
        type: record.type || '-',
      });
    }
  }, [record]);

  const safeJsonParse = (value) => {
    try {
      return JSON.parse(value || '{}');
    } catch {
      return {};
    }
  };

  const handleChange = useCallback(
    (field) => (e) => {
      const value = e.target.value;
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleSubmit = async () => {
    try {
      const { id } = record;
      const { archive_allow_encrypted, allowed_file_size, ...restFormData } = formData;

      const parsedFileSize = safeJsonParse(allowed_file_size);

      const payload = {
        id,
        ...restFormData,
        archive_allow_encrypted: archive_allow_encrypted === 'yes',
        allowed_file_size: JSON.stringify(parsedFileSize),
      };

      const { message = '' } = await axiosMailPostCall('updateMailFilters', payload);
      if (message === 'Email filters updated successfully') {
        renderSuccessMessage(message);
        onUpdate();
        handleClose();
      }
    } catch (error) {
      console.error('Update failed:', error);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Edit Mail Filter</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="mail-type-label">Mail Type</InputLabel>
                <Select labelId="mail-type-label" value={formData.type} label="Mail Type" disabled>
                  <MenuItem value="INBOUND">Inbound</MenuItem>
                  <MenuItem value="OUTBOUND">Outbound</MenuItem>
                  <MenuItem value="-">-</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="archive-encrypted-label">Archive Allow Encrypted</InputLabel>
                <Select
                  labelId="archive-encrypted-label"
                  value={formData.archive_allow_encrypted}
                  label="Archive Allow Encrypted"
                  onChange={handleChange('archive_allow_encrypted')}
                >
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {columns.map(({ name, label }) => (
              <Grid key={name} item xs={12} sm={6}>
                <TextField fullWidth label={label} value={formData[name]} onChange={handleChange(name)} />
              </Grid>
            ))}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Allowed File Size (JSON)"
                multiline
                rows={4}
                value={formData.allowed_file_size}
                onChange={handleChange('allowed_file_size')}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailUpdate;
